import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { FiArrowDown } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import { projectsData } from "../../data/projectsData";
import leftArrow from "../../assets/icons/left.svg";
import rightArrow from "../../assets/icons/right.svg";
import "./ProjectDetails.css";
import { getProjects } from "../../api/projects";
import { resolveMediaUrl } from "../../utils/media";

const ProjectDetails = () => {
  const { projectId } = useParams();
  const { i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.language) === "rtl";
  const fallbackProject = projectsData.find((item) => item.id === projectId);
  const [apiProject, setApiProject] = useState(null);
  const [resolved, setResolved] = useState(Boolean(fallbackProject));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setResolved(Boolean(fallbackProject));

    const loadProject = async () => {
      try {
        const data = await getProjects({ language: String(i18n.language || "en").toLowerCase() });
        const list = Array.isArray(data) ? data : [];
        const matched = list.find(
          (item) =>
            String(item?.projectKey || item?.id || "") === String(projectId)
        );
        if (isMounted) {
          setApiProject(matched || null);
          setResolved(true);
        }
      } catch (_error) {
        if (isMounted) {
          setApiProject(null);
          setResolved(true);
        }
      }
    };

    loadProject();
    return () => {
      isMounted = false;
    };
  }, [fallbackProject, i18n.language, projectId]);

  const project = useMemo(() => {
    if (!fallbackProject && !apiProject) return null;
    if (!apiProject) return fallbackProject;

    const gallery = Array.isArray(apiProject?.images) ? apiProject.images : [];
    const galleryUrls = gallery
      .map((img) => resolveMediaUrl(img?.imageUrl))
      .filter(Boolean);
    const cover = resolveMediaUrl(apiProject?.coverImageUrl);
    const mainImage = cover || galleryUrls[0] || fallbackProject?.image || projectsData[0]?.image;
    const projectImages = [mainImage, ...galleryUrls].filter(
      (img, index, arr) => Boolean(img) && arr.indexOf(img) === index
    );

    if (!fallbackProject) {
      return {
        id: String(apiProject?.projectKey || apiProject?.id || projectId),
        title: apiProject?.title || "Project",
        description: apiProject?.description || "",
        highlight: apiProject?.highlight || "",
        location: apiProject?.location || "",
        sector: apiProject?.sector || "",
        image: mainImage,
        images: projectImages.length ? projectImages : [mainImage],
      };
    }

    return {
      ...fallbackProject,
      title: apiProject?.title || fallbackProject.title,
      description: apiProject?.description || fallbackProject.description,
      highlight: apiProject?.highlight || fallbackProject.highlight,
      location: apiProject?.location || fallbackProject.location,
      sector: apiProject?.sector || fallbackProject.sector,
      image: mainImage,
      images: projectImages.length ? projectImages : [mainImage],
    };
  }, [apiProject, fallbackProject, projectId]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [projectId, i18n.language]);

  if (!project && !resolved) return null;
  if (!project) return <Navigate to="/allprojects" replace />;

  const galleryImages = Array.isArray(project.images) && project.images.length ? project.images : [project.image];
  const safeIndex = Math.min(currentImageIndex, galleryImages.length - 1);
  const currentImage = galleryImages[safeIndex] || project.image;

  const showPrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };

  const showNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div className="projectDetailsPage">
      <Navbar theme="light" />

      <section className="projectDetails-hero">
        <img src={currentImage} alt={project.title} className="projectDetails-heroImage" />
        <div className="projectDetails-heroOverlay" aria-hidden="true" />
        <div className="projectDetails-heroInner">
          <h1>{project.title}</h1>
          <button type="button">
            <span>View details</span>
            <FiArrowDown />
          </button>
        </div>
      </section>

      <main className="projectDetails-page-content-guide">
        <div className="projectDetails">
          <section className="projectDetails-content">
            <div className="projectDetails-meta">
              <h4>Location</h4>
              <p>{project.location}</p>
              <h4>Sector</h4>
              <p>{project.sector}</p>
            </div>

            <div className="projectDetails-text">
              <p>{project.description}</p>
              <p className="highlight">{project.highlight}</p>
            </div>
          </section>

          <section className="projectDetails-gallery">
            <div className="projectDetails-arrows">
              <button type="button" aria-label="Previous image" onClick={showPrevImage}>
                <img src={isRtl ? rightArrow : leftArrow} alt="" />
              </button>
              <button type="button" aria-label="Next image" onClick={showNextImage}>
                <img src={isRtl ? leftArrow : rightArrow} alt="" />
              </button>
            </div>

            <img src={currentImage} alt={project.title} className="projectDetails-mainImage" />
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectDetails;

